import type { TFunction } from 'i18next';
import { api } from '@/lib/api';
import { managedHelpTopicClientId } from './help-managed-topic-id';
import type { HelpCategory } from './help-center-topics';
import { resolveHelpCenterIcon } from './resolve-help-center-icon';

export type HelpCenterManagedTopicDto = {
  id: string;
  categoryId: string;
  title: string;
  description: string;
  formationUrl: string | null;
  legacyTopicId: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type HelpCenterManagedCategoryDto = {
  id: string;
  title: string;
  description: string;
  iconKey: string;
  sortOrder: number;
  isActive: boolean;
  legacyCategoryId: string | null;
  createdAt: string;
  updatedAt: string;
  topics: HelpCenterManagedTopicDto[];
};

export async function fetchPublicManagedHelpCategories(): Promise<HelpCenterManagedCategoryDto[]> {
  try {
    const res = await api.get<{ success: boolean; data: { categories: HelpCenterManagedCategoryDto[] } }>(
      '/public/help-center/managed',
    );
    return res.data?.categories ?? [];
  } catch {
    return [];
  }
}

/** Carte topicId UI → URL formation (pour le lanceur). */
export function buildManagedFormationUrlMap(
  categories: HelpCenterManagedCategoryDto[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const c of categories) {
    for (const t of c.topics) {
      const url = (t.formationUrl ?? '').trim();
      if (!url) continue;
      const clientId = t.legacyTopicId ?? managedHelpTopicClientId(t.id);
      map[clientId] = url;
    }
  }
  return map;
}

/**
 * Convertit l’API publique en rubriques au format centre d’aide (titres i18n si clés legacy).
 */
export function helpCenterApiRowsToCategories(
  categories: HelpCenterManagedCategoryDto[],
  t: TFunction,
): HelpCategory[] {
  return categories.map((c) => ({
    id: c.legacyCategoryId ?? `managed-cat:${c.id}`,
    title: c.legacyCategoryId ? t(`help.categories.${c.legacyCategoryId}.title`, c.title) : c.title,
    description: c.legacyCategoryId
      ? t(`help.categories.${c.legacyCategoryId}.description`, c.description)
      : c.description,
    icon: resolveHelpCenterIcon(c.iconKey),
    topics: c.topics.map((topic) => {
      const id = topic.legacyTopicId ?? managedHelpTopicClientId(topic.id);
      return {
        id,
        title: topic.legacyTopicId ? t(`help.topics.${topic.legacyTopicId}.title`, topic.title) : topic.title,
        description: topic.legacyTopicId
          ? t(`help.topics.${topic.legacyTopicId}.description`, topic.description)
          : topic.description,
      };
    }),
  }));
}

